import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { PortfolioMilestone } from '@models';
import { Study } from '@models';
import { StudyService } from '@services';
import { columnConfigurationsMilestones } from '@utils';
import { PortfolioMilestoneFilterService } from 'app/services/portfolio-milestone-filter/portfolio-milestone-filter.service';
import { Subject, takeUntil } from 'rxjs';

interface MilestoneGroup {
  protocolNumber: string;
  parentClientName: string;
  milestones: PortfolioMilestone[];
  expanded: boolean;
}

@Component({
  selector: 'kpi-table-card',
  templateUrl: './kpi-table-card.component.html',
  styleUrl: './kpi-table-card.component.scss'
})
export class KpiTableCardComponent implements OnInit, OnDestroy {
  private readonly initialRenderBatchSize = 120;
  private readonly incrementalRenderBatchSize = 120;
  private readonly groupedInitialRenderBatchSize = 80;
  private readonly groupedIncrementalRenderBatchSize = 80;
  private readonly groupedExpandChunkSize = 120;
  @Input() title = 'Milestone summary table';
  @Input() mode: 'card' | 'fullscreen' = 'card';
  @Input() showHeader = true;
  @Input() showExpandButton = true;
  @Input() hasData = true;
  @Input() enableSort = true;
  @Input() infoTooltipTitle = '';
  @Input() infoTooltipText = '';

  private studyService = inject(StudyService);
  private milestoneFilterService = inject(PortfolioMilestoneFilterService);
  private destroy$ = new Subject<void>();
  private filteredMilestones: PortfolioMilestone[] = [];
  private sortedMilestones: PortfolioMilestone[] = [];
  private visibleMilestonesLimit = this.initialRenderBatchSize;
  private visibleGroupsLimit = this.groupedInitialRenderBatchSize;

  // Agrupamiento
  isGrouped = false;
  groupedData: MilestoneGroup[] = [];
  dataSource = new MatTableDataSource<PortfolioMilestone>([]); // solo para compatibilidad
  displayedColumns: (keyof PortfolioMilestone)[] = [];
  allColumns: (keyof PortfolioMilestone)[] = Object.keys(
    columnConfigurationsMilestones
  ) as (keyof PortfolioMilestone)[];
  columnConfigurations = columnConfigurationsMilestones;

  // Protocol tooltip state
  protocolTooltipElement: PortfolioMilestone | null = null;
  protocolTooltipStudy: Study | null = null;
  protocolTooltipOpen = false;
  cachedStudies: Study[] = [];
  private protocolTooltipHideTimeoutId: any = null;
  private isProtocolAnchorHovered = false;
  private isProtocolTooltipHovered = false;

  // Sorting state
  currentSortColumn: string | null = null;
  currentSortDirection: 'asc' | 'desc' | null = null;
  infoTooltipOpen = false;

  ngOnInit(): void {
    // Obtener los estudios del cache para tener acceso a protocolDesc
    this.studyService
      .getCachedStudiesPortfolio()
      .pipe(takeUntil(this.destroy$))
      .subscribe((studies) => {
        if (studies) {
          this.cachedStudies = studies;
        }
      });

    // Obtener todos los milestones del servicio
    this.updateDisplayedColumns();
    this.studyService
      .getPortfolioMilestones(this.studyService.getParentClientNames())
      .pipe(takeUntil(this.destroy$))
      .subscribe((milestones: PortfolioMilestone[]) => {
        // Inicializar el servicio de filtros con todos los milestones
        this.milestoneFilterService.initializeFilters(milestones);

        // Suscribirse al estado filtrado
        this.milestoneFilterService.filterState$
          .pipe(takeUntil(this.destroy$))
          .subscribe((filterState) => {
            this.filteredMilestones = filterState.filteredData;
            this.resetVisibleMilestones();
            this.resetVisibleGroups();
            this.applySorting();
            if (this.isGrouped) {
              this.groupedData = this.groupMilestonesByProtocol(this.sortedMilestones);
            }
            this.hasData = filterState.filteredData.length > 0;
          });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.protocolTooltipHideTimeoutId) {
      window.clearTimeout(this.protocolTooltipHideTimeoutId);
    }
  }

  updateDisplayedColumns(): void {
    const clientNamesList = this.studyService.getClientNamesList();
    if (clientNamesList.length > 1) {
      this.displayedColumns = [...this.allColumns];
    } else {
      this.displayedColumns = this.allColumns.filter((_, index) => index !== 0); // Ocultar la columna parentClientName si solo hay un cliente
    }
  }

  /**
   * Retorna las columnas a mostrar en la vista agrupada (sin protocolNumber y parentClientName)
   */
  get groupedDisplayedColumns(): (keyof PortfolioMilestone)[] {
    return this.displayedColumns.filter(
      (col) => col !== 'protocolNumber' && col !== 'parentClientName'
    );
  }

  get showGroupedClientLabel(): boolean {
    return this.displayedColumns.includes('parentClientName');
  }

  @Output() expandClicked = new EventEmitter<void>();

  get hasInfoTooltip(): boolean {
    return this.infoTooltipText.trim().length > 0;
  }

  onInfoEnter(): void {
    this.infoTooltipOpen = true;
  }

  onInfoLeave(): void {
    this.infoTooltipOpen = false;
  }

  onInfoToggle(): void {
    this.infoTooltipOpen = !this.infoTooltipOpen;
  }

  // Protocol tooltip handlers
  onProtocolMouseEnter(element: PortfolioMilestone): void {
    this.isProtocolAnchorHovered = true;

    if (this.protocolTooltipHideTimeoutId) {
      window.clearTimeout(this.protocolTooltipHideTimeoutId);
      this.protocolTooltipHideTimeoutId = null;
    }

    this.protocolTooltipElement = element;

    // Find the corresponding study from cache to get protocolDesc
    this.protocolTooltipStudy =
      this.cachedStudies.find((study) => String(study.studySk) === String(element.studySk)) || null;

    this.protocolTooltipOpen = true;
  }

  onProtocolMouseLeave(): void {
    this.isProtocolAnchorHovered = false;
    this.protocolTooltipHide();
  }

  onProtocolTooltipHover(): void {
    this.isProtocolTooltipHovered = true;

    if (this.protocolTooltipHideTimeoutId) {
      window.clearTimeout(this.protocolTooltipHideTimeoutId);
      this.protocolTooltipHideTimeoutId = null;
    }
  }

  onProtocolTooltipLeave(): void {
    this.isProtocolTooltipHovered = false;
    this.protocolTooltipHide();
  }

  private protocolTooltipHide(): void {
    if (this.protocolTooltipHideTimeoutId) {
      window.clearTimeout(this.protocolTooltipHideTimeoutId);
      this.protocolTooltipHideTimeoutId = null;
    }

    if (this.isProtocolAnchorHovered || this.isProtocolTooltipHovered) {
      return;
    }

    this.protocolTooltipHideTimeoutId = window.setTimeout(() => {
      this.protocolTooltipOpen = false;
      this.protocolTooltipHideTimeoutId = null;
    }, 150);
  }

  // Sorting handler
  onMenuSort(direction: 'asc' | 'desc', column: string): void {
    if (this.currentSortColumn === column && this.currentSortDirection === direction) {
      // Reset to default sort if same direction is selected twice
      this.currentSortColumn = null;
      this.currentSortDirection = null;
    } else {
      this.currentSortColumn = column;
      this.currentSortDirection = direction;
    }

    // Apply sorting to dataSource
    this.applySorting();
  }

  private applySorting(): void {
    const baseData = [...this.filteredMilestones];

    if (!this.currentSortColumn || !this.currentSortDirection) {
      // Reset to original order
      this.sortedMilestones = baseData;
      this.updateVisibleRows();
      return;
    }

    const data = [...baseData];
    const column = this.currentSortColumn as keyof PortfolioMilestone;
    const ascending = this.currentSortDirection === 'asc';

    data.sort((a, b) => {
      const aValue = a[column];
      const bValue = b[column];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return ascending ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return ascending ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    this.sortedMilestones = data;
    this.updateVisibleRows();
  }

  private resetVisibleMilestones(): void {
    this.visibleMilestonesLimit = this.initialRenderBatchSize;
  }

  private updateVisibleRows(): void {
    if (this.isGrouped) {
      this.dataSource.data = this.sortedMilestones;
      return;
    }

    const visibleCount = Math.min(this.visibleMilestonesLimit, this.sortedMilestones.length);
    this.dataSource.data = this.sortedMilestones.slice(0, visibleCount);
  }

  private loadMoreRows(): void {
    if (this.visibleMilestonesLimit >= this.sortedMilestones.length) {
      return;
    }

    this.visibleMilestonesLimit = Math.min(
      this.visibleMilestonesLimit + this.incrementalRenderBatchSize,
      this.sortedMilestones.length
    );
    this.updateVisibleRows();
  }

  private resetVisibleGroups(): void {
    this.visibleGroupsLimit = this.groupedInitialRenderBatchSize;
  }

  private loadMoreGroups(): void {
    if (this.visibleGroupsLimit >= this.groupedData.length) {
      return;
    }

    this.visibleGroupsLimit = Math.min(
      this.visibleGroupsLimit + this.groupedIncrementalRenderBatchSize,
      this.groupedData.length
    );
  }

  onTableScroll(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const distanceToBottom = target.scrollHeight - (target.scrollTop + target.clientHeight);
    if (distanceToBottom > 120) {
      return;
    }

    if (this.isGrouped) {
      this.loadMoreGroups();
      return;
    }

    this.loadMoreRows();
  }

  /**
   * Agrupa los milestones por protocolNumber y parentClientName
   */
  groupMilestonesByProtocol(milestones: PortfolioMilestone[]): MilestoneGroup[] {
    const groupsMap = new Map<string, MilestoneGroup>();
    for (const milestone of milestones) {
      const protocol = milestone.protocolNumber || 'N/A';
      const client = milestone.parentClientName || 'N/A';
      const key = `${protocol}__${client}`;
      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          protocolNumber: protocol,
          parentClientName: client,
          milestones: [],
          expanded: true
        });
      }
      groupsMap.get(key)!.milestones.push(milestone);
    }
    return Array.from(groupsMap.values());
  }

  /**
   * Alterna la expansión de un grupo
   */
  toggleGroup(group: MilestoneGroup): void {
    group.expanded = !group.expanded;
  }

  /**
   * Convierte un valor a string para uso en el template
   */
  toString(value: any): string {
    return value != null ? String(value) : '';
  }

  /**
   * Alterna entre vista agrupada y vista normal
   */
  toggleGrouping(grouped: boolean): void {
    this.isGrouped = grouped;
    if (grouped) {
      this.groupedData = this.groupMilestonesByProtocol(this.sortedMilestones);
      this.resetVisibleGroups();
      return;
    }

    this.resetVisibleMilestones();
    this.updateVisibleRows();
  }

  /**
   * Expande todos los grupos
   */
  expandAllGroups(): void {
    this.toggleGroupsInChunks(true);
  }

  /**
   * Colapsa todos los grupos
   */
  collapseAllGroups(): void {
    this.groupedData.forEach((group) => {
      group.expanded = false;
    });
  }

  private toggleGroupsInChunks(expanded: boolean): void {
    let startIndex = 0;
    const applyChunk = () => {
      const endIndex = Math.min(startIndex + this.groupedExpandChunkSize, this.groupedData.length);
      for (let idx = startIndex; idx < endIndex; idx += 1) {
        this.groupedData[idx].expanded = expanded;
      }
      startIndex = endIndex;

      if (startIndex < this.groupedData.length) {
        setTimeout(applyChunk, 0);
      }
    };

    applyChunk();
  }

  onExpand(): void {
    this.expandClicked.emit();
  }

  trackByMilestone(index: number, item: PortfolioMilestone): string {
    return [
      item.studySk ?? '',
      item.protocolNumber ?? '',
      item.milestoneName ?? '',
      item.status ?? '',
      item.baselineDate ?? '',
      item.plannedDate ?? '',
      item.actualDate ?? ''
    ].join('|');
  }

  trackByGroup(index: number, group: MilestoneGroup): string {
    return `${group.protocolNumber}|${group.parentClientName}`;
  }

  get visibleMilestonesCount(): number {
    return this.dataSource.data.length;
  }

  get totalMilestonesCount(): number {
    return this.sortedMilestones.length;
  }

  get hasMoreMilestonesToRender(): boolean {
    return !this.isGrouped && this.visibleMilestonesCount < this.totalMilestonesCount;
  }

  get visibleGroupedData(): MilestoneGroup[] {
    return this.groupedData.slice(0, this.visibleGroupsLimit);
  }

  get visibleGroupsCount(): number {
    return this.visibleGroupedData.length;
  }

  get totalGroupsCount(): number {
    return this.groupedData.length;
  }

  get hasMoreGroupsToRender(): boolean {
    return this.isGrouped && this.visibleGroupsCount < this.totalGroupsCount;
  }
}
